import { create } from 'zustand';
import type { FleetScope } from '@/mappers/scope';

type PlantMode = 'single' | 'field';
type ShellMode = 'fleet' | PlantMode;

const key = (mode: PlantMode) => `pro-plant:${mode}`;
const FLEET_KEY = 'pro-fleet-scope';
const MODE_KEY = 'pro-mode';

function read(k: string): string | null {
	if (typeof window === 'undefined') return null;
	try {
		return localStorage.getItem(k);
	} catch {
		return null;
	}
}

function write(k: string, v: string) {
	try {
		localStorage.setItem(k, v);
	} catch {
		// Storage can be unavailable (private window): the choice then lasts for the session.
	}
}

interface PlantStore {
	selected: Record<PlantMode, string | null>;
	select: (mode: PlantMode, id: string) => void;
	/** The Fleet select: `__all`, `__allsingle`, `__allfield` or a plant id. */
	fleetScope: FleetScope;
	selectFleetScope: (scope: FleetScope) => void;
	/** The header mode the user last chose (the shell validates it against the permitted modes). */
	mode: ShellMode | null;
	setMode: (mode: ShellMode) => void;
}

/** Plant selected in the header for each dashboard mode, the Fleet scope and the mode, persisted. */
export const usePlantStore = create<PlantStore>((set) => ({
	selected: { single: read(key('single')), field: read(key('field')) },
	select: (mode, id) => {
		write(key(mode), id);
		set((s) => ({ selected: { ...s.selected, [mode]: id } }));
	},
	fleetScope: read(FLEET_KEY) ?? '__all',
	selectFleetScope: (scope) => {
		write(FLEET_KEY, scope);
		set({ fleetScope: scope });
	},
	mode: (read(MODE_KEY) as ShellMode | null) ?? null,
	setMode: (mode) => {
		write(MODE_KEY, mode);
		set({ mode });
	},
}));
